import { Link } from '@tanstack/react-router';
import { Flag, MessageSquare, Paperclip } from 'lucide-react';
import type { Task } from '@shared/types';
import {
  DUE_CLASSES,
  PRIORITY_CLASSES,
  PRIORITY_LABELS,
  cn,
  dueLabel,
  dueStatus,
} from '@/lib/format';
import { AvatarGroup, LabelChip } from './ui';

interface TaskRowProps {
  task: Task;
  onToggleDone: (task: Task) => void;
  /** Shown in cross-project views where the project isn't implied. */
  projectName?: string;
  dense?: boolean;
}

export function TaskRow({ task, onToggleDone, projectName, dense = false }: TaskRowProps) {
  const status = dueStatus(task.dueDate, task.done);

  return (
    <div
      className={cn(
        'group flex items-start gap-3 px-4 transition-colors hover:bg-[var(--surface-hover)]',
        dense ? 'py-2' : 'py-2.5',
        // Client feedback on the legacy app: completed tasks should recede
        // rather than compete with open ones.
        task.done && 'opacity-55',
      )}
    >
      <input
        type="checkbox"
        checked={task.done}
        onChange={() => onToggleDone(task)}
        className="mt-0.5 size-4 shrink-0 cursor-pointer rounded border-[var(--line-strong)]
                   accent-brand-500 text-brand-500 focus-visible:outline-brand-500"
        aria-label={task.done ? `${task.title} als offen markieren` : `${task.title} als erledigt markieren`}
      />

      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <Link
            to="/tasks/$taskId"
            params={{ taskId: task.id }}
            className={cn(
              'truncate text-sm text-strong hover:text-brand-500 hover:underline',
              task.done && 'line-through decoration-1',
            )}
          >
            {task.title}
          </Link>
          <span className="shrink-0 font-mono text-[11px] text-faint">{task.identifier}</span>
        </div>

        {/* Metadata row. Everything here is conditional so a bare task stays
            a single clean line rather than showing empty placeholders. */}
        {(task.labels.length > 0 || projectName || task.priority > 0) && (
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            {projectName && (
              <span className="rounded surface-sunken px-1.5 py-px text-[10px] font-medium text-muted">
                {projectName}
              </span>
            )}
            {task.priority > 0 && (
              <span
                className={cn(
                  'inline-flex items-center gap-0.5 text-[10px] font-medium',
                  PRIORITY_CLASSES[task.priority],
                )}
              >
                <Flag className="size-2.5" aria-hidden />
                {PRIORITY_LABELS[task.priority]}
              </span>
            )}
            {task.labels.map((l) => (
              <LabelChip key={l.id} label={l} size="sm" />
            ))}
          </div>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-2.5 pt-0.5">
        {task.attachments && task.attachments.length > 0 && (
          <span className="flex items-center gap-0.5 text-[11px] text-faint" title="Anhänge">
            <Paperclip className="size-3" aria-hidden />
            {task.attachments.length}
          </span>
        )}
        {task.subtasks && task.subtasks.length > 0 && (
          <span
            className="flex items-center gap-0.5 text-[11px] text-faint"
            title={`${task.subtasks.filter((s) => s.done).length} von ${task.subtasks.length} Teilaufgaben erledigt`}
          >
            <MessageSquare className="size-3" aria-hidden />
            {task.subtasks.filter((s) => s.done).length}/{task.subtasks.length}
          </span>
        )}
        {task.dueDate && (
          <span className={cn('whitespace-nowrap text-[11px]', DUE_CLASSES[status])}>
            {dueLabel(task.dueDate, task.done)}
          </span>
        )}
        <AvatarGroup users={task.assignees} max={2} />
      </div>
    </div>
  );
}
